import React, { useState, useEffect } from 'react';
import API_URL from './config';
import { BrowserRouter as Router, Routes, Route, Navigate, useParams } from 'react-router-dom';
import axios from 'axios';
import Login from './components/Login';
import ForgotPassword from './components/ForgotPassword';
import ResetPassword from './components/ResetPassword';
import Dashboard from './components/Dashboard';
import MyClients from './components/MyClients';
import UserManagement from './components/UserManagement';
import Logs from './components/Logs';
import RecruiterPortal from './components/RecruiterPortal';
import Reports from './components/Reports';
import ClientInterviewView from './components/ClientInterviewView';
import ClientPortal from './components/ClientPortal';
import OnboardingInformation from './components/OnboardingInformation';
import OnboardingTable from './components/OnboardingTable';
import ProtectedContent from './components/ProtectedContent';
import PublicCandidateProfile from './components/PublicCandidateProfile';
import MountainResume from './components/MountainResume';
import CompanyPage from './components/CompanyPage';
import LinehaulLandingPage from './components/LinehaulLandingPage';
import PDLandingPage from './components/PDLandingPage';
import CompanyPagesAdmin from './components/CompanyPagesAdmin';
import CandidateArchive from './components/CandidateArchive';
import PlaybookPage from './components/PlaybookPage';
import ResourcesPage from './components/ResourcesPage';
import AILab from './components/AILab';
import FAKnowledgePage from './components/FAKnowledgePage';
import AccountsPage from './components/AccountsPage';
import StationMap from './components/StationMap';
import ProposalsPage from './components/ProposalsPage';
import BlogAgent from './components/BlogAgent';
import FadvActivity from './components/FadvActivity';
import TaskInbox from './components/TaskInbox';
import MessageTemplates from './components/MessageTemplates';
import IntroTexts from './components/IntroTexts';
import PriorCandidates from './components/PriorCandidates';
import MessagesInbox from './components/MessagesInbox';
import Inbound from './components/Inbound';
import Linehaul from './components/Linehaul';
import UpdateBanner from './components/UpdateBanner';
import Books from './components/Books';
import './App.css';
import './styles/shared-components.css';

// Axios interceptor for auto-refresh on 401 errors
let isRefreshing = false;
let failedQueue = [];

const processQueue = (error, token = null) => {
  failedQueue.forEach(prom => {
    if (error) {
      prom.reject(error);
    } else {
      prom.resolve(token);
    }
  });
  failedQueue = [];
};

axios.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;

    // If error is 401 and we haven't tried to refresh yet
    if (error.response?.status === 401 && !originalRequest._retry) {
      if (isRefreshing) {
        // If already refreshing, queue this request
        return new Promise((resolve, reject) => {
          failedQueue.push({ resolve, reject });
        }).then(token => {
          originalRequest.headers['Authorization'] = 'Bearer ' + token;
          return axios(originalRequest);
        }).catch(err => {
          return Promise.reject(err);
        });
      }

      originalRequest._retry = true;
      isRefreshing = true;

      try {
        const response = await fetch(`${API_URL}/api/auth/refresh`, {
          method: 'POST',
          credentials: 'include'
        });

        if (response.ok) {
          const data = await response.json();
          const newToken = data.token;
          localStorage.setItem('token', newToken);
          axios.defaults.headers.common['Authorization'] = 'Bearer ' + newToken;
          originalRequest.headers['Authorization'] = 'Bearer ' + newToken;
          processQueue(null, newToken);
          isRefreshing = false;
          return axios(originalRequest);
        } else {
          // Refresh failed, redirect to login
          processQueue(error, null);
          isRefreshing = false;
          localStorage.removeItem('token');
          window.location.href = '/login';
          return Promise.reject(error);
        }
      } catch (err) {
        processQueue(err, null);
        isRefreshing = false;
        localStorage.removeItem('token');
        window.location.href = '/login';
        return Promise.reject(err);
      }
    }

    return Promise.reject(error);
  }
);

// Routes /:slug to MountainResume if slug ends in -resume, else PublicCandidateProfile.
// -resume links no longer force a redirect to the full /login page when logged out --
// MountainResume itself shows an inline password-only prompt (see resume-password-login).
function SlugRouter() {
  const { slug } = useParams();
  if (slug && slug.endsWith('-resume')) {
    return <MountainResume />;
  }
  return <PublicCandidateProfile />;
}

function App() {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Try to refresh token on app load (silent refresh)
    const attemptRefresh = async () => {
      try {
        const response = await fetch(`${API_URL}/api/auth/refresh`, {
          method: 'POST',
          credentials: 'include' // Important: send cookies
        });

        if (response.ok) {
          const data = await response.json();
          if (data.token && data.user) {
            localStorage.setItem('token', data.token);
            setUser(data.user);
            setLoading(false);
            return;
          }
        }
      } catch (err) {
        console.log('Silent refresh failed, checking existing token');
      }

      // If refresh failed, check for existing token
      const token = localStorage.getItem('token');
      if (token) {
        fetch(`${API_URL}/api/auth/me`, {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        })
        .then(res => res.json())
        .then(data => {
          if (data.user) {
            setUser(data.user);
          }
          setLoading(false);
        })
        .catch(() => {
          localStorage.removeItem('token');
          setLoading(false);
        });
      } else {
        setLoading(false);
      }
    };

    attemptRefresh();
  }, []);

  // Heartbeat - send activity ping every 2 minutes
  useEffect(() => {
    if (!user) return;

    const sendHeartbeat = async () => {
      try {
        const token = localStorage.getItem('token');
        await axios.post(`${API_URL}/api/activity/heartbeat`, {}, {
          headers: { Authorization: `Bearer ${token}` }
        });
      } catch (err) {
        console.error('Heartbeat error:', err);
      }
    };

    // Send initial heartbeat
    sendHeartbeat();

    // Set up interval for every 2 minutes
    const interval = setInterval(sendHeartbeat, 2 * 60 * 1000);

    return () => clearInterval(interval);
  }, [user]);

  const handleLogin = (userData) => {
    setUser(userData);
  };

  const handleLogout = async () => {
    try {
      const token = localStorage.getItem('token');
      // Call logout API to end session
      await axios.post(`${API_URL}/api/auth/logout`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
    } catch (err) {
      console.error('Logout error:', err);
    } finally {
      localStorage.removeItem('token');
      setUser(null);
    }
  };

  if (loading) {
    return <div className="loading">Loading...</div>;
  }

  return (
    <Router>
      <div className="App">
        <UpdateBanner />
        <Routes>
          <Route
            path="/login"
            element={user ? <Navigate to="/" /> : <Login onLogin={handleLogin} />}
          />
          <Route path="/forgot-password" element={<ForgotPassword />} />
          <Route path="/reset-password" element={<ResetPassword />} />
          <Route
            path="/"
            element={
              user ? (
                user.role?.toLowerCase() === 'customer' ? (
                  <Navigate to="/client-portal" />
                ) : (
                  <Navigate to="/recruiter-portal" />
                )
              ) : (
                <Navigate to="/login" />
              )
            }
          />
          <Route
            path="/dashboard"
            element={
              user && user.role?.toLowerCase() === 'admin' ? (
                <ProtectedContent user={user}>
                  <Dashboard user={user} onLogout={handleLogout} />
                </ProtectedContent>
              ) : (
                <Navigate to="/recruiter-portal" />
              )
            }
          />
          <Route
            path="/my-clients"
            element={
              user && user.role?.toLowerCase() === 'admin' ? (
                <ProtectedContent user={user}>
                  <MyClients user={user} onLogout={handleLogout} />
                </ProtectedContent>
              ) : (
                <Navigate to="/" />
              )
            }
          />
          <Route
            path="/users"
            element={
              user && ['admin', 'recruiting_admin'].includes(user.role?.toLowerCase()) ? (
                <ProtectedContent user={user}>
                  <UserManagement user={user} onLogout={handleLogout} />
                </ProtectedContent>
              ) : (
                <Navigate to="/" />
              )
            }
          />
          <Route
            path="/logs"
            element={
              user && user.role?.toLowerCase() === 'admin' ? (
                <ProtectedContent user={user}>
                  <Logs user={user} onLogout={handleLogout} />
                </ProtectedContent>
              ) : (
                <Navigate to="/" />
              )
            }
          />
          <Route
            path="/recruiter-portal"
            element={
              user && (['admin', 'recruiter', 'recruiting_admin'].includes(user.role?.toLowerCase())) ? (
                <ProtectedContent user={user}>
                  <RecruiterPortal user={user} onLogout={handleLogout} />
                </ProtectedContent>
              ) : (
                <Navigate to="/" />
              )
            }
          />
          <Route
            path="/reports"
            element={
              user && (['admin', 'recruiter', 'recruiting_admin'].includes(user.role?.toLowerCase())) ? (
                <Reports user={user} onLogout={handleLogout} />
              ) : (
                <Navigate to="/" />
              )
            }
          />
          <Route
            path="/client-portal"
            element={
              user && user.role?.toLowerCase() === 'customer' ? (
                <ClientPortal user={user} onLogout={handleLogout} />
              ) : (
                <Navigate to="/" />
              )
            }
          />
          <Route
            path="/client-interviews"
            element={
              user ? (
                <ClientInterviewView user={user} onLogout={handleLogout} />
              ) : (
                <Navigate to="/login" />
              )
            }
          />
          <Route
            path="/onboarding"
            element={
              user ? (
                <OnboardingInformation user={user} onLogout={handleLogout} />
              ) : (
                <Navigate to="/login" />
              )
            }
          />
          <Route
            path="/onboarding-table"
            element={
              user && ['admin', 'recruiter', 'recruiting_admin'].includes(user.role?.toLowerCase()) ? (
                <ProtectedContent user={user}>
                  <OnboardingTable user={user} onLogout={handleLogout} />
                </ProtectedContent>
              ) : (
                <Navigate to="/" />
              )
            }
          />
          <Route
            path="/candidate-archive"
            element={
              user && (['admin', 'recruiter', 'recruiting_admin'].includes(user.role?.toLowerCase())) ? (
                <ProtectedContent user={user}>
                  <CandidateArchive user={user} onLogout={handleLogout} />
                </ProtectedContent>
              ) : (
                <Navigate to="/" />
              )
            }
          />
          <Route
            path="/company-pages"
            element={
              user && user.role?.toLowerCase() === 'admin' ? (
                <ProtectedContent user={user}>
                  <CompanyPagesAdmin user={user} onLogout={handleLogout} />
                </ProtectedContent>
              ) : (
                <Navigate to="/" />
              )
            }
          />
          <Route
            path="/accounts"
            element={
              user && (['admin', 'recruiter', 'recruiting_admin'].includes(user.role?.toLowerCase())) ? (
                <ProtectedContent user={user}>
                  <AccountsPage user={user} onLogout={handleLogout} />
                </ProtectedContent>
              ) : (
                <Navigate to="/" />
              )
            }
          />
          <Route
            path="/books"
            element={
              user && ['ajroy@mountainrecruiting.com', 'monica@mountainrecruiting.com'].includes(String(user.email || '').toLowerCase()) ? (
                <Books user={user} onLogout={handleLogout} />
              ) : (
                <Navigate to="/" />
              )
            }
          />
          <Route
            path="/playbook"
            element={
              user && user.role?.toLowerCase() === 'admin' ? (
                <ProtectedContent user={user}>
                  <PlaybookPage user={user} onLogout={handleLogout} />
                </ProtectedContent>
              ) : (
                <Navigate to="/" />
              )
            }
          />
          <Route
            path="/ai-lab"
            element={
              user && (['admin', 'recruiter', 'recruiting_admin'].includes(user.role?.toLowerCase())) ? (
                <ProtectedContent user={user}>
                  <AILab user={user} onLogout={handleLogout} />
                </ProtectedContent>
              ) : (
                <Navigate to="/" />
              )
            }
          />
          <Route
            path="/resources"
            element={
              user && (['admin', 'recruiter', 'recruiting_admin'].includes(user.role?.toLowerCase())) ? (
                <ProtectedContent user={user}>
                  <ResourcesPage user={user} onLogout={handleLogout} />
                </ProtectedContent>
              ) : (
                <Navigate to="/" />
              )
            }
          />
          <Route
            path="/fa-knowledge"
            element={
              user && (['admin', 'recruiter', 'recruiting_admin'].includes(user.role?.toLowerCase())) ? (
                <ProtectedContent user={user}>
                  <FAKnowledgePage user={user} onLogout={handleLogout} />
                </ProtectedContent>
              ) : (
                <Navigate to="/" />
              )
            }
          />
          <Route
            path="/station-map"
            element={
              user && (['admin', 'recruiter', 'recruiting_admin'].includes(user.role?.toLowerCase())) ? (
                <StationMap user={user} onLogout={handleLogout} />
              ) : (
                <Navigate to="/" />
              )
            }
          />
          <Route
            path="/message-templates"
            element={
              user && (['admin', 'recruiter', 'recruiting_admin'].includes(user.role?.toLowerCase())) ? (
                <ProtectedContent user={user}>
                  <MessageTemplates user={user} onLogout={handleLogout} />
                </ProtectedContent>
              ) : (
                <Navigate to="/" />
              )
            }
          />
          <Route
            path="/intro-texts"
            element={
              user && (['admin', 'recruiter', 'recruiting_admin'].includes(user.role?.toLowerCase())) ? (
                <ProtectedContent user={user}>
                  <IntroTexts user={user} onLogout={handleLogout} />
                </ProtectedContent>
              ) : (
                <Navigate to="/" />
              )
            }
          />
          <Route
            path="/prior-candidates"
            element={
              user && (['admin', 'recruiter', 'recruiting_admin'].includes(user.role?.toLowerCase())) ? (
                <ProtectedContent user={user}>
                  <PriorCandidates user={user} onLogout={handleLogout} />
                </ProtectedContent>
              ) : (
                <Navigate to="/" />
              )
            }
          />
          <Route
            path="/messages"
            element={
              user && (['admin', 'recruiter', 'recruiting_admin'].includes(user.role?.toLowerCase())) ? (
                <ProtectedContent user={user}>
                  <MessagesInbox user={user} onLogout={handleLogout} />
                </ProtectedContent>
              ) : (
                <Navigate to="/" />
              )
            }
          />
          <Route
            path="/proposals"
            element={
              user && user.role?.toLowerCase() === 'admin' ? (
                <ProtectedContent user={user}>
                  <ProposalsPage user={user} onLogout={handleLogout} />
                </ProtectedContent>
              ) : (
                <Navigate to="/" />
              )
            }
          />
          <Route
            path="/inbound"
            element={
              user && user.role?.toLowerCase() === 'admin' ? (
                <Inbound user={user} onLogout={handleLogout} />
              ) : (
                <Navigate to="/" />
              )
            }
          />
          <Route
            path="/linehaul"
            element={
              user && user.role?.toLowerCase() === 'admin' ? (
                <Linehaul user={user} onLogout={handleLogout} />
              ) : (
                <Navigate to="/" />
              )
            }
          />
          <Route
            path="/blog-agent"
            element={
              user && user.role?.toLowerCase() === 'admin' ? (
                <BlogAgent user={user} onLogout={handleLogout} />
              ) : (
                <Navigate to="/" />
              )
            }
          />
          <Route
            path="/fadv-activity"
            element={
              user && (['admin', 'recruiter', 'recruiting_admin'].includes(user.role?.toLowerCase())) ? (
                <FadvActivity user={user} onLogout={handleLogout} />
              ) : (
                <Navigate to="/" />
              )
            }
          />
          <Route
            path="/tasks"
            element={
              user && (['admin', 'recruiter', 'recruiting_admin'].includes(user.role?.toLowerCase())) ? (
                <ProtectedContent user={user}>
                  <TaskInbox user={user} onLogout={handleLogout} />
                </ProtectedContent>
              ) : (
                <Navigate to="/" />
              )
            }
          />
          <Route
            path="/companies/:slug"
            element={<CompanyPage />}
          />
          <Route
            path="/linehaul-apply"
            element={<LinehaulLandingPage />}
          />
          <Route
            path="/pd-apply"
            element={<PDLandingPage />}
          />
          <Route
            path="/:slug"
            element={<SlugRouter />}
          />
        </Routes>
      </div>
    </Router>
  );
}

export default App;
